import { Link } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import type { GroupSummary, GroupTotals, GroupWithMembers } from "../../types";
import { COMPLETED_GROUP_STATUS, GROUP_STATUS_SHORT_LABELS } from "../../types";
import { PageHeader } from "../PageHeader";
import { Button } from "../Button";
import { Alert } from "../Alert";
import { IhqTypePill, LockPill, StatusPill, TagPill } from "../Pills";
import { SalesforceRecordLink } from "../SalesforceRecordLink";
import { formatDate, formatInteger } from "../../utils/format";
import { GRADIENT_DOE_EXISTS_TOOLTIP } from "../../utils/gradientDoe";

interface GroupOverviewHeaderProps {
  group: GroupWithMembers;
  totals: GroupTotals | null;
  /** Other versions on the same Opportunity, oldest first. */
  otherVersions: readonly GroupSummary[];
  isLocked: boolean;
  lockedTitle?: string;
  currentUserId: string | null;
  isAddingGradient: boolean;
  isClaiming: boolean;
  isReopening: boolean;
  onAddMember: () => void;
  onAddGradientDoe: () => void;
  onClaim: () => void;
  onEditGroup: () => void;
  onSendToSales: () => void;
  onReopen: () => void;
  onRestore: () => void;
  onViewSentCensus: () => void;
  children?: never;
}

export function GroupOverviewHeader({ group, totals, otherVersions, isLocked, lockedTitle, currentUserId, isAddingGradient, isClaiming, isReopening, onAddMember, onAddGradientDoe, onClaim, onEditGroup, onSendToSales, onReopen, onRestore, onViewSentCensus }: GroupOverviewHeaderProps) {
  const isSuperseded = group.supersededByGroupId !== null;
  const hasNewerVersion = otherVersions.some((entry) => entry.version > group.version);
  const hasGradientDoe = group.members.some((member) => member.memberType === "GradientDoe");
  const isCompleted = group.status === COMPLETED_GROUP_STATUS;
  const canClaim = !isLocked && group.underwriterUserId === null && currentUserId !== null;

  return (
    <>
      <PageHeader
        title={group.name}
        badges={
          <>
            <StatusPill status={group.status} full />
            <LockPill group={group} />
            <IhqTypePill ihqType={group.ihqType} />
            {group.hasSrxCarveOut && <TagPill label="SRx carve-out" tone="warning" title="Specialty Rx carve-out, from the latest Quote version in Salesforce (Has SRx Carveout)" />}
            <VersionsDropdown group={group} totals={totals} otherVersions={otherVersions} />
          </>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>Effective {formatDate(group.effectiveDate)}</span>
            <span className="inline-flex items-center gap-1">
              Opportunity <SalesforceRecordLink object="Opportunity" recordId={group.opportunityId} />
            </span>
            <span className="inline-flex items-center gap-2">
              Underwriter: {group.underwriterName ?? "Unassigned"}
              {canClaim && (
                <Button variant="ghost" size="sm" onClick={onClaim} isLoading={isClaiming}>
                  Assign to me
                </Button>
              )}
            </span>
            <span className="inline-flex items-center gap-1">
              Quote Owner: <SalesforceRecordLink object="User" recordId={group.quoteOwnerUserId} label={group.quoteOwnerName} />
            </span>
          </span>
        }
        actions={
          <>
            <Button onClick={onAddMember} disabled={isLocked} title={lockedTitle}>
              Add New Member
            </Button>
            <Button variant="secondary" onClick={onAddGradientDoe} disabled={isLocked || hasGradientDoe} isLoading={isAddingGradient} title={lockedTitle ?? (hasGradientDoe ? GRADIENT_DOE_EXISTS_TOOLTIP : undefined)}>
              Add Gradient Doe
            </Button>
            <Button variant="secondary" onClick={onEditGroup} disabled={isLocked} title={lockedTitle}>
              Edit Group
            </Button>
            {isCompleted && (
              <Button variant="secondary" onClick={onViewSentCensus} title="What Sales received for this version">
                View Sent Census
              </Button>
            )}
            {hasNewerVersion && (
              <Button variant="secondary" onClick={onRestore} title="Copy this version's members and ratings into the latest version, or start a new version from it">
                Use This Version
              </Button>
            )}
            {isCompleted && !isSuperseded ? (
              <Button variant="secondary" onClick={onReopen} isLoading={isReopening} title="Start a new working version of this group">
                Reopen as New Version
              </Button>
            ) : (
              <Button variant="secondary" onClick={onSendToSales} disabled={isLocked} title={lockedTitle}>
                Send to Sales
              </Button>
            )}
          </>
        }
      />
      {isSuperseded && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message={
            <span>
              This is version {group.version}. A newer version replaced it, so it is read-only.{" "}
              <Link to={`/uw/groups/${group.supersededByGroupId}`} className="font-medium text-brand-navy underline-offset-2 hover:underline">
                Open the current version
              </Link>
              .
            </span>
          }
        />
      )}
    </>
  );
}

interface VersionsDropdownProps {
  group: GroupWithMembers;
  totals: GroupTotals | null;
  otherVersions: readonly GroupSummary[];
  children?: never;
}

interface VersionRow {
  id: string;
  version: number;
  status: GroupSummary["status"];
  completedUtc: string | null;
  memberCount: number;
  totalDebits: number;
  isCurrent: boolean;
}

const VERSION_CELL = "px-3 py-2 text-left";

/**
 * Every version of this group on the Opportunity, oldest first, with what an
 * underwriter compares versions by: when it went to Sales, how many members,
 * and total debit points. Picking a row opens that version.
 */
function VersionsDropdown({ group, totals, otherVersions }: VersionsDropdownProps) {
  const rows: VersionRow[] = [
    ...otherVersions.map((entry) => ({ id: entry.id, version: entry.version, status: entry.status, completedUtc: entry.completedUtc, memberCount: entry.memberCount, totalDebits: entry.totalDebits, isCurrent: false })),
    { id: group.id, version: group.version, status: group.status, completedUtc: group.completedUtc, memberCount: totals?.totalMembers ?? 0, totalDebits: totals?.totalDebits ?? 0, isCurrent: true },
  ].sort((a, b) => a.version - b.version);
  const count = rows.length;

  return (
    <Menu>
      <MenuButton className={`inline-flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${group.supersededByGroupId !== null ? "border-gray-200 text-gray-500" : "border-gray-300 text-gray-700"}`} title={count > 1 ? "You are on this version. Open the list to switch versions." : "The only version of this group"}>
        <span className="font-mono">v{group.version}</span>
        <span className="text-gray-500">of {count}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </MenuButton>
      <MenuItems anchor="bottom start" className="z-50 mt-1 min-w-[26rem] overflow-hidden rounded-lg bg-white text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
        <div role="none" className="grid grid-cols-[4rem_1fr_6rem_7rem] border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
          <span className={VERSION_CELL}>Version</span>
          <span className={VERSION_CELL}>Submitted</span>
          <span className={`${VERSION_CELL} text-right`}>Members</span>
          <span className={`${VERSION_CELL} text-right`}>Debits</span>
        </div>
        {rows.map((row) => (
          <MenuItem key={row.id}>
            <Link to={`/uw/groups/${row.id}`} aria-current={row.isCurrent ? "page" : undefined} className={`grid grid-cols-[4rem_1fr_6rem_7rem] items-center border-b border-gray-100 last:border-b-0 data-[focus]:bg-brand-green-10 ${row.isCurrent ? "bg-brand-green-05 font-semibold" : ""}`}>
              <span className={`${VERSION_CELL} font-mono text-xs`}>v{row.version}</span>
              <span className={VERSION_CELL}>
                {row.completedUtc ? formatDate(row.completedUtc) : <span className="text-gray-500">Not submitted</span>}
                <span className="block text-xs font-normal text-gray-500">{GROUP_STATUS_SHORT_LABELS[row.status]}{row.isCurrent ? " (open)" : ""}</span>
              </span>
              <span className={`${VERSION_CELL} text-right tabular-nums`}>{formatInteger(row.memberCount)}</span>
              <span className={`${VERSION_CELL} text-right tabular-nums`}>{formatInteger(row.totalDebits)}</span>
            </Link>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
